import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import { CheckCircle, ArrowRight, Mail, Rocket } from 'lucide-react';
import { Link } from 'react-router-dom';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';

const PaymentSuccessPage = () => {
  return (
    <>
      <Helmet>
        <title>Payment Successful — Welcome to Searchefy!</title>
        <meta
          name="description"
          content="Your payment was successful. Create your Searchefy account to get started."
        />
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>

      <div data-testid="payment-success-page" className="min-h-screen bg-[#F9FAFB]">
        <Navbar />

        <main className="pt-24 pb-24">
          <div className="max-w-2xl mx-auto px-6 md:px-12">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.5 }}
              className="text-center"
            >
              {/* Success Icon */}
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ delay: 0.2, type: 'spring', stiffness: 200 }}
                className="inline-flex items-center justify-center w-24 h-24 rounded-full bg-green-100 mb-8"
              >
                <CheckCircle size={48} className="text-green-500" />
              </motion.div>

              <h1 className="font-display text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
                Payment Successful!
              </h1>
              <p className="text-gray-600 text-lg mb-8">
                Thank you for your purchase. You now have lifetime access to Searchefy.
              </p>

              {/* What's Next Card */}
              <div className="bg-white rounded-2xl border border-gray-200 p-8 mb-8 text-left">
                <h2 className="font-display text-xl font-bold text-gray-900 mb-6 flex items-center gap-2">
                  <Rocket size={24} className="text-blue-500" />
                  What's Next?
                </h2>

                <div className="space-y-6">
                  <div className="flex gap-4">
                    <div className="flex-shrink-0 w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center text-blue-600 font-bold text-sm">
                      1
                    </div>
                    <div>
                      <h3 className="font-medium text-gray-900 mb-1">
                        Create Your Account
                      </h3>
                      <p className="text-gray-600 text-sm">
                        Use the same email you used for payment to create your Searchefy account.
                      </p>
                    </div>
                  </div>

                  <div className="flex gap-4">
                    <div className="flex-shrink-0 w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center text-blue-600 font-bold text-sm">
                      2
                    </div>
                    <div>
                      <h3 className="font-medium text-gray-900 mb-1">
                        Explore the Directory
                      </h3>
                      <p className="text-gray-600 text-sm">
                        Browse 500+ curated backlink sources filtered by DA, PA, and link type.
                      </p>
                    </div>
                  </div>

                  <div className="flex gap-4">
                    <div className="flex-shrink-0 w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center text-blue-600 font-bold text-sm">
                      3
                    </div>
                    <div>
                      <h3 className="font-medium text-gray-900 mb-1">
                        Create Your First Project
                      </h3>
                      <p className="text-gray-600 text-sm">
                        Add your target URL and start tracking backlink opportunities.
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* CTA */}
              <a
                href="https://app.searchefy.com/auth/signup"
                data-testid="payment-success-cta"
                className="btn-primary inline-flex items-center gap-2 text-lg px-10 py-5 mb-6"
              >
                Create My Account
                <ArrowRight size={20} />
              </a>

              <p className="text-sm text-gray-500 flex items-center justify-center gap-2">
                <Mail size={16} />
                Check your email for a receipt from Dodo Payments
              </p>

              {/* Support Note */}
              <div className="mt-12 p-6 bg-gray-50 rounded-xl">
                <p className="text-sm text-gray-600">
                  Having trouble? Contact us at{' '}
                  <a
                    href="mailto:thepratishjain@gmail.com"
                    className="text-blue-600 hover:underline"
                  >
                    thepratishjain@gmail.com
                  </a>{' '}
                  and we'll help you get started.
                </p>
              </div>

              {/* Back to Home */}
              <div className="mt-8">
                <Link
                  to="/"
                  className="text-gray-500 hover:text-gray-700 text-sm"
                >
                  ← Back to Home
                </Link>
              </div>
            </motion.div>
          </div>
        </main>

        <Footer />
      </div>
    </>
  );
};

export default PaymentSuccessPage;
